/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Pagination as MuiPagination, PaginationProps } from "@mui/material";
import PaginationItem from "@mui/material/PaginationItem";
import React from "react";
import { Link, useLocation } from "react-router-dom";

import Analytics from "../../services/analytics";
import { TestIds } from "../../testIds";

import { BackButton, ForwardButton } from "./PageDirectionButtons";

interface Props extends PaginationProps {
  page: number;
  path?: string;
  onClickPageNumber?: (page: number) => void;
}

/**
 * A customized pagination component using Material-UI Pagination that supports adding analytics events
 * and provides navigation icons for the first and last pages.
 *
 * @component
 * @example
 * // Example usage:
 * <Pagination
 *   page={2}  // Current active page
 *   count={10}  // Total number of pages
 *   onChange={handlePageChange}  // Callback for handling page changes
 * />
 *
 * @param {Object} props - The props for the Pagination component.
 * @param {number} props.page - The current active page.
 * @param {Function} props.onChange - Callback function triggered when the page changes.
 * @returns {JSX.Element} The Pagination component.
 */
const Pagination: React.FC<Props> = ({ page, path, onClickPageNumber, ...props }) => {
  const location = useLocation();

  return (
    <MuiPagination
      sx={{ mt: 2 }}
      color="primary"
      size="large"
      page={page}
      onChange={props.onChange}
      renderItem={(item) => {
        const nextParams = new URLSearchParams(location.search);
        nextParams.set("page", String(item.page));
        const pageQuery = nextParams.toString();
        return (
          <PaginationItem
            data-testid={`${TestIds.components.pagination.number}`}
            data-testdata={`${item.page}`}
            component={path ? Link : "div"}
            to={path ? path + "?" + pageQuery : undefined}
            {...item}
            onClick={() => {
              if (onClickPageNumber && item.page) {
                onClickPageNumber(item.page);
              }
              Analytics.push({
                dataLayer: {
                  event: "pagination_by",
                  pagination_by_number: item.page,
                },
              });
            }}
            slots={{ previous: BackButton, next: ForwardButton }}
          />
        );
      }}
      {...props}
    />
  );
};

export default Pagination;
